import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input, Radio, message, Select } from 'antd';
import { UserCircleIcon, CameraIcon } from '../components/icons';
import AccentButton from '../components/AccentButton/AccentButton';
import AccentButtonDark from '../components/AccentButtonDark/AccentButtonDark';
import { useAuth } from '../contexts/AuthContext';
import { list_unclaimed_musicians, list_unclaimed_bands, list_genres, list_instruments, upload_profile_photo, Musician, Band, Genre, Instrument } from '../api';
import './SignUp.css';

type AccountType = 'musician' | 'fan';

const BANNER_TEXT: Record<AccountType, string> = {
    musician: 'Create a musician profile to be added to bands',
    fan: 'Fans can add photos and comments to shows',
};

const SignUp: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { signUp } = useAuth();
    const initialType = (location.state as any)?.accountType === 'fan' ? 'fan' : 'musician';
    const [accountType, setAccountType] = useState<AccountType>(initialType);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Musician profile fields (for new profiles only)
    const [bio, setBio] = useState('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [soundcloudUrl, setSoundcloudUrl] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
    const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);

    // Genres and instruments lists
    const [genres, setGenres] = useState<Genre[]>([]);
    const [instruments, setInstruments] = useState<Instrument[]>([]);
    const [loadingGenres, setLoadingGenres] = useState(false);
    const [loadingInstruments, setLoadingInstruments] = useState(false);

    // Musician claiming state
    const [unclaimedMusicians, setUnclaimedMusicians] = useState<Musician[]>([]);
    const [loadingMusicians, setLoadingMusicians] = useState(false);
    const [selectedMusicianId, setSelectedMusicianId] = useState<string | null>(null);
    const [musicianSearch, setMusicianSearch] = useState('');

    // Band claiming state
    const [unclaimedBands, setUnclaimedBands] = useState<Band[]>([]);
    const [loadingBands, setLoadingBands] = useState(false);
    const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
    const [bandSearch, setBandSearch] = useState('');

    // Load unclaimed musicians, genres, and instruments when musician tab is selected
    useEffect(() => {
        if (accountType === 'musician') {
            setLoadingMusicians(true);
            list_unclaimed_musicians()
                .then(musicians => {
                    setUnclaimedMusicians(musicians);
                })
                .catch(err => {
                    console.error('Failed to load unclaimed musicians:', err);
                })
                .finally(() => {
                    setLoadingMusicians(false);
                });

            setLoadingGenres(true);
            list_genres()
                .then(data => {
                    setGenres(data);
                })
                .catch(err => {
                    console.error('Failed to load genres:', err);
                })
                .finally(() => {
                    setLoadingGenres(false);
                });

            setLoadingInstruments(true);
            list_instruments()
                .then(data => {
                    setInstruments(data);
                })
                .catch(err => {
                    console.error('Failed to load instruments:', err);
                })
                .finally(() => {
                    setLoadingInstruments(false);
                });

            setLoadingBands(true);
            list_unclaimed_bands()
                .then(bands => {
                    setUnclaimedBands(bands);
                })
                .catch(err => {
                    console.error('Failed to load unclaimed bands:', err);
                })
                .finally(() => {
                    setLoadingBands(false);
                });
        }
    }, [accountType]);

    // Filter musicians by search
    const filteredMusicians = unclaimedMusicians.filter(m => {
        const fullName = `${m.first_name} ${m.last_name}`.toLowerCase();
        return fullName.includes(musicianSearch.trim().toLowerCase());
    });

    const selectedMusician = unclaimedMusicians.find(m => m.id === selectedMusicianId);

    // Filter bands by search
    const filteredBands = unclaimedBands.filter(b =>
        b.name.toLowerCase().includes(bandSearch.trim().toLowerCase())
    );

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                message.error('Please select an image file');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                message.error('Image must be less than 5MB');
                return;
            }
            setPhotoFile(file);
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setPhotoPreview(previewUrl);
        }
    };

    const handleSignUp = async () => {
        if (!email || !password) {
            message.error('Email and password are required');
            return;
        }

        if (!name && accountType === 'fan') {
            message.error('Name is required');
            return;
        }

        setSubmitting(true);
        try {
            // Upload photo if selected
            let photoUrl: string | undefined;
            if (photoFile && accountType === 'musician' && !selectedMusicianId) {
                setUploadingPhoto(true);
                try {
                    photoUrl = await upload_profile_photo(photoFile);
                } catch (uploadError: any) {
                    message.error('Failed to upload photo: ' + uploadError.message);
                    setSubmitting(false);
                    setUploadingPhoto(false);
                    return;
                }
                setUploadingPhoto(false);
            }

            await signUp({
                email,
                password,
                displayName: name || (selectedMusician ? `${selectedMusician.first_name} ${selectedMusician.last_name}` : undefined),
                accountType,
                musicianId: selectedMusicianId || undefined,
                bandId: selectedBandId || undefined,
                // Only include musician profile fields when creating new profile
                ...(accountType === 'musician' && !selectedMusicianId ? {
                    bio: bio || undefined,
                    photo_url: photoUrl || undefined,
                    soundcloud_url: soundcloudUrl || undefined,
                    genres: selectedGenres.length > 0 ? selectedGenres : undefined,
                    instruments: selectedInstruments.length > 0 ? selectedInstruments : undefined,
                } : {}),
            });
            message.success('Account created successfully!');
            navigate('/home');
        } catch (error: any) {
            message.error(error.message || 'Failed to create account');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="signup-page">
            <div className="signup-content">
                <h1 className="signup-title">Sign Up</h1>

                {/* Account Type Tabs */}
                <div className="signup-tabs">
                    <button
                        className={`signup-tab ${accountType === 'musician' ? 'signup-tab-active' : ''}`}
                        onClick={() => setAccountType('musician')}
                    >
                        <UserCircleIcon size="m" tone={accountType === 'musician' ? 'black' : 'lightGrey'} />
                        <span>Musician</span>
                    </button>
                    <button
                        className={`signup-tab ${accountType === 'fan' ? 'signup-tab-active' : ''}`}
                        onClick={() => setAccountType('fan')}
                    >
                        <UserCircleIcon size="m" tone={accountType === 'fan' ? 'black' : 'lightGrey'} />
                        <span>Fan</span>
                    </button>
                </div>

                {/* Dynamic Banner */}
                <div className="signup-banner">
                    <span className="signup-banner-text">{BANNER_TEXT[accountType]}</span>
                </div>

                {/* Form Fields */}
                <div className="signup-form">
                    {/* Musician Claiming Section */}
                    {accountType === 'musician' && (
                        <div className="signup-field">
                            <label className="signup-label">Claim Your Profile</label>
                            <span className="signup-label-hint">
                                Select your name if you're already in our system, or leave empty to create a new profile
                            </span>
                            {loadingMusicians ? (
                                <div className="signup-loading">Loading musicians...</div>
                            ) : (
                                <>
                                    <Input
                                        placeholder="Search musicians..."
                                        value={musicianSearch}
                                        onChange={(e) => setMusicianSearch(e.target.value)}
                                        style={{ marginBottom: 8 }}
                                    />
                                    <div className="signup-musician-list">
                                        {/* Option to not claim any profile */}
                                        <button
                                            className={`signup-musician-item${selectedMusicianId === null ? ' signup-musician-item--selected' : ''}`}
                                            onClick={() => setSelectedMusicianId(null)}
                                        >
                                            <div className="signup-musician-avatar signup-musician-avatar--placeholder" />
                                            <span className="signup-musician-name">Create new profile</span>
                                            <Radio
                                                checked={selectedMusicianId === null}
                                                className="signup-musician-radio"
                                            />
                                        </button>
                                        {filteredMusicians.map((musician) => (
                                            <button
                                                key={musician.id}
                                                className={`signup-musician-item${selectedMusicianId === musician.id ? ' signup-musician-item--selected' : ''}`}
                                                onClick={() => setSelectedMusicianId(musician.id)}
                                            >
                                                {musician.photo_url ? (
                                                    <img
                                                        className="signup-musician-avatar"
                                                        src={musician.photo_url}
                                                        alt={`${musician.first_name} ${musician.last_name}`}
                                                    />
                                                ) : (
                                                    <div className="signup-musician-avatar signup-musician-avatar--placeholder" />
                                                )}
                                                <span className="signup-musician-name">
                                                    {musician.first_name} {musician.last_name}
                                                </span>
                                                <Radio
                                                    checked={selectedMusicianId === musician.id}
                                                    className="signup-musician-radio"
                                                />
                                            </button>
                                        ))}
                                        {filteredMusicians.length === 0 && musicianSearch && (
                                            <div className="signup-empty-list">No musicians found</div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Band Selection Section - only show for musicians after they've selected a musician profile */}
                    {accountType === 'musician' && (
                        <div className="signup-field">
                            <label className="signup-label">
                                Join a Band <span className="signup-label-optional">(Optional)</span>
                            </label>
                            <span className="signup-label-hint">
                                Select a band to claim, or skip and join bands later
                            </span>
                            {loadingBands ? (
                                <div className="signup-loading">Loading bands...</div>
                            ) : (
                                <>
                                    <Input
                                        placeholder="Search bands..."
                                        value={bandSearch}
                                        onChange={(e) => setBandSearch(e.target.value)}
                                        style={{ marginBottom: 8 }}
                                    />
                                    <div className="signup-band-list">
                                        {/* Option to skip band selection */}
                                        <button
                                            className={`signup-band-item${selectedBandId === null ? ' signup-band-item--selected' : ''}`}
                                            onClick={() => setSelectedBandId(null)}
                                        >
                                            <div className="signup-band-avatar signup-band-avatar--placeholder" />
                                            <div className="signup-band-info">
                                                <span className="signup-band-name">Skip - join bands later</span>
                                            </div>
                                            <Radio
                                                checked={selectedBandId === null}
                                                className="signup-band-radio"
                                            />
                                        </button>
                                        {filteredBands.map((band) => (
                                            <button
                                                key={band.id}
                                                className={`signup-band-item${selectedBandId === band.id ? ' signup-band-item--selected' : ''}`}
                                                onClick={() => setSelectedBandId(band.id)}
                                            >
                                                {band.photo_url ? (
                                                    <img
                                                        className="signup-band-avatar"
                                                        src={band.photo_url}
                                                        alt={band.name}
                                                    />
                                                ) : (
                                                    <div className="signup-band-avatar signup-band-avatar--placeholder" />
                                                )}
                                                <div className="signup-band-info">
                                                    <span className="signup-band-name">{band.name}</span>
                                                    {band.genre && (
                                                        <span className="signup-band-genre">{band.genre}</span>
                                                    )}
                                                </div>
                                                <Radio
                                                    checked={selectedBandId === band.id}
                                                    className="signup-band-radio"
                                                />
                                            </button>
                                        ))}
                                        {filteredBands.length === 0 && bandSearch && (
                                            <div className="signup-empty-list">No bands found</div>
                                        )}
                                        {filteredBands.length === 0 && !bandSearch && unclaimedBands.length === 0 && (
                                            <div className="signup-empty-list">No unclaimed bands available</div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="signup-field">
                        <label className="signup-label">Name</label>
                        <Input
                            placeholder={selectedMusician ? `${selectedMusician.first_name} ${selectedMusician.last_name}` : "Full Name"}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={!!selectedMusician}
                        />
                        {selectedMusician && (
                            <span className="signup-label-hint">Name will be set from your claimed profile</span>
                        )}
                    </div>

                    <div className="signup-field">
                        <label className="signup-label">Email</label>
                        <Input
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="signup-field">
                        <label className="signup-label">Password</label>
                        <Input.Password
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    {/* Musician Profile Fields - only show when creating new profile */}
                    {accountType === 'musician' && !selectedMusicianId && (
                        <>
                            <div className="signup-field">
                                <label className="signup-label">Bio</label>
                                <span className="signup-label-hint">Optional - Tell us about yourself</span>
                                <Input.TextArea
                                    placeholder="A brief bio about yourself as a musician..."
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <div className="signup-field">
                                <label className="signup-label">Profile Photo</label>
                                <span className="signup-label-hint">Optional - Upload your profile photo</span>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoSelect}
                                    style={{ display: 'none' }}
                                />
                                <div className="signup-photo-picker">
                                    {photoPreview ? (
                                        <img
                                            src={photoPreview}
                                            alt="Profile preview"
                                            className="signup-photo-preview"
                                            onClick={() => fileInputRef.current?.click()}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="signup-photo-button"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <CameraIcon size="l" tone="darkGrey" />
                                            <span>Add Photo</span>
                                        </button>
                                    )}
                                    {photoFile && (
                                        <button
                                            type="button"
                                            className="signup-photo-remove"
                                            onClick={() => {
                                                setPhotoFile(null);
                                                setPhotoPreview(null);
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = '';
                                                }
                                            }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="signup-field">
                                <label className="signup-label">SoundCloud Song</label>
                                <span className="signup-label-hint">Optional - Link to one of your songs on SoundCloud</span>
                                <Input
                                    placeholder="https://soundcloud.com/artist/song-name"
                                    value={soundcloudUrl}
                                    onChange={(e) => setSoundcloudUrl(e.target.value)}
                                />
                            </div>

                            <div className="signup-field">
                                <label className="signup-label">Genres</label>
                                <span className="signup-label-hint">Optional - Select genres you play</span>
                                <Select
                                    mode="multiple"
                                    placeholder="Select genres..."
                                    value={selectedGenres}
                                    onChange={setSelectedGenres}
                                    loading={loadingGenres}
                                    options={genres.map(g => ({ label: g.name, value: g.name }))}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div className="signup-field">
                                <label className="signup-label">Instruments</label>
                                <span className="signup-label-hint">Optional - Select instruments you play</span>
                                <Select
                                    mode="multiple"
                                    placeholder="Select instruments..."
                                    value={selectedInstruments}
                                    onChange={setSelectedInstruments}
                                    loading={loadingInstruments}
                                    options={instruments.map(i => ({ label: i.name, value: i.name }))}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}

                    <AccentButton
                        size="large"
                        block
                        shape="round"
                        onClick={handleSignUp}
                        loading={submitting}
                        disabled={submitting}
                    >
                        {uploadingPhoto ? 'Uploading Photo...' : submitting ? 'Creating Account...' : 'Sign Up'}
                    </AccentButton>
                </div>

                {/* Already have account */}
                <div className="signup-divider signup-footer-divider">
                    <span className="signup-divider-line" />
                    <span className="signup-divider-text">already have account?</span>
                    <span className="signup-divider-line" />
                </div>

                <AccentButtonDark
                    block
                    size="large"
                    shape="round"
                    onClick={() => navigate('/signin')}
                >
                    Sign In
                </AccentButtonDark>
            </div>
        </div>
    );
};

export default SignUp;
